import React, { useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import moment from "moment";
import "moment/locale/gu";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Filter,
  ListFilter,
  ArrowUpDown,
  Package,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchProcessesListData,
  getProcesses,
  getTotalProcesses,
  getProcessesFilter,
  setProcessesFilter,
  getProcessesLoading,
  setEditProcess,
  toggleProcessModal,
} from "../../features/processes/processesSlice";
import {
  fetchProcessDefinitions,
  getProcessDefinitions,
} from "../../features/processDefinitions/processDefinitionsSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { useCanAccess } from "../../hooks/useCanAccess";
import useConfirmPopup from "../../context/useConfirmPopup";
import { DELETE, POST, PUT } from "../../services/axios.service";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { localizedProcessName } from "../../lib/utils";
import { t } from "../../locales";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ProcessesProps, ProcessesFilterType, IFormInput } from "../../types/process";
import ProcessFormModal from "./ProcessFormModal";

const sortByOptions = [
  { value: "id", label: t.processes.sortOptions.id },
  { value: "name", label: t.processes.sortOptions.name },
  { value: "created_at", label: t.processes.sortOptions.created_at },
];

const sortOrderOptions = [
  { value: "asc", label: t.processes.sortOrderOptions.asc },
  { value: "desc", label: t.processes.sortOrderOptions.desc },
];

const PROCESS_TYPE_BADGE_CLASS: Record<string, string> = {
  buffing: "bg-amber-50 text-amber-800 border-amber-200",
  platting: "bg-cyan-50 text-cyan-700 border-cyan-200",
};

const FALLBACK_PROCESS_BADGE_CLASS = "bg-gray-100 text-gray-700 border-gray-200";

const ProcessList = () => {
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const confirmDelete = useConfirmPopup();

  moment.locale("gu");

  const processes = useAppSelector(getProcesses);
  const totalProcesses = useAppSelector(getTotalProcesses);
  const filter = useAppSelector(getProcessesFilter);
  const loading = useAppSelector(getProcessesLoading);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const processDefinitions = useAppSelector(getProcessDefinitions);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number>(0);

  useEffect(() => {
    dispatch(fetchProcessDefinitions({ all: true }));
  }, [dispatch]);

  const typeFilterOptions = processDefinitions
    .filter((d) => d.is_active)
    .map((d) => ({ value: d.slug, label: localizedProcessName(d) }));

  // process.type mirrors the linked process_definition's slug — look the
  // display label up from the live definitions list instead of assuming
  // it's always "buffing" or "platting".
  const typeLabelBySlug: Record<string, string> = {};
  processDefinitions.forEach((d) => {
    typeLabelBySlug[d.slug] = localizedProcessName(d);
  });

  const { register, control, handleSubmit, reset } =
    useForm<ProcessesFilterType>({
      defaultValues: { ...filter },
    });

  const fetchProcesses = () => {
    dispatch(
      fetchProcessesListData({
        page,
        size: pageSize,
        search: filter.search,
        type: filter.type,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
      })
    );
  };

  const prevBranchIdRef = useRef<number | null>(null);
  const selectedBranchId = selectedBranch?.id ?? null;

  useEffect(() => {
    if (!selectedBranchId) return;

    const prevBranchId = prevBranchIdRef.current;
    prevBranchIdRef.current = selectedBranchId;

    const isBranchChange =
      prevBranchId !== null && prevBranchId !== selectedBranchId;
    if (isBranchChange && page !== 1) {
      setPage(1);
      return;
    }

    fetchProcesses();
  }, [page, pageSize, filter, selectedBranchId]);

  const onApplyFilters = (data: ProcessesFilterType) => {
    dispatch(
      setProcessesFilter({
        search: data.search || null,
        type: data.type || null,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
      })
    );
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaults: ProcessesFilterType = {
      search: null,
      type: null,
      sortBy: "id",
      sortOrder: "desc",
    };
    reset(defaults);
    dispatch(setProcessesFilter(defaults));
    setPage(1);
  };

  const handleShowProcessForm = async () => {
    await dispatch(setEditProcess(null));
    dispatch(toggleProcessModal(true));
  };

  const handleSaveProcess = async (data: IFormInput) => {
    try {
      const storeEditProcess = (await import("../../app/store")).store.getState()
        .processes.editProcess;

      if (storeEditProcess && storeEditProcess.id) {
        const response: any = await PUT(`/v1/processes/${storeEditProcess.id}`, {
          name: data.name,
          process_definition_id: data.process_definition_id,
          mode_policy: data.mode_policy || null,
          next_process_definition_ids: data.next_process_definition_ids || [],
          next_process_mandatory: data.next_process_mandatory === "" ? null : data.next_process_mandatory,
          description: data.description,
        });
        if (response.data.status === "success") {
          await dispatch(setEditProcess(null));
          if (page === 1) {
            fetchProcesses();
          } else {
            setPage(1);
          }
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } else {
        const response: any = await POST("/v1/processes", {
          name: data.name,
          process_definition_id: data.process_definition_id,
          mode_policy: data.mode_policy || null,
          next_process_definition_ids: data.next_process_definition_ids || [],
          next_process_mandatory: data.next_process_mandatory === "" ? null : data.next_process_mandatory,
          description: data.description,
        });
        if (response.data.status === "success") {
          if (page === 1) {
            fetchProcesses();
          } else {
            setPage(1);
          }
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.data?.message ||
          t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
  };

  const handleDeleteProcess = async (id: number) => {
    const confirmed = await confirmDelete(
      t.processes.deleteConfirmText,
      t.processes.deleteConfirmTitle,
      t.processes.deleteConfirmButton
    );

    if (confirmed) {
      setActionLoadingId(id);
      try {
        const response: any = await DELETE(`/v1/processes/${id}`);
        if (response.data.status === "success") {
          toast.success(response.data.message, { ...(toastStyles as any) });
          const newPage =
            processes.length > 1 ? page : Math.max(page - 1, 1);
          if (newPage === page) {
            fetchProcesses();
          } else {
            setPage(newPage);
          }
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || t.processes.inUseError,
          { ...(toastStyles as any) }
        );
      }
      setActionLoadingId(0);
    }
  };

  const handleEditProcess = async (process: ProcessesProps) => {
    await dispatch(setEditProcess(process));
    dispatch(toggleProcessModal(true));
  };

  const totalPages = Math.ceil(totalProcesses / pageSize);

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const start = totalProcesses === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalProcesses);

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-3 ml-auto">
          <Button
            variant="outline"
            className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {t.processes.filterToggle}
          </Button>

          {canAccess("processes.create") && (
            <Button
              className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              onClick={handleShowProcessForm}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t.processes.addNewProcess}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t.processes.filterTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.processes.searchLabel}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      className="pl-12 h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                      placeholder={t.processes.searchPlaceholder}
                      {...register("search")}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.processes.typeFilterLabel}
                  </label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={typeFilterOptions}
                          placeholder={t.processes.typeFilterPlaceholder}
                          value={
                            typeFilterOptions.find((o) => o.value === field.value) || null
                          }
                          onChange={(opt: any) => field.onChange(opt?.value || null)}
                          isClearable
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.processes.sortByLabel}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortBy"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortByOptions}
                          placeholder={t.processes.sortByPlaceholder}
                          value={sortByOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "id")
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.processes.sortOrderLabel}
                  </label>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortOrder"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortOrderOptions}
                          placeholder={t.processes.sortOrderPlaceholder}
                          value={sortOrderOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "desc")
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
                  onClick={handleClearFilters}
                >
                  {t.processes.clearFilters}
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
                >
                  {t.processes.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && processes.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.processes.loading}
              </div>
            </CardContent>
          </Card>
        ) : !loading && processes.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {t.processes.noRecords}
            </CardContent>
          </Card>
        ) : (
          <div
            className={`space-y-3${
              loading ? " opacity-50 pointer-events-none" : ""
            }`}
          >
            {processes.map((process: ProcessesProps, index: number) => (
              <Card key={process.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="font-medium text-gray-900">
                        {process.name || "-"}
                      </span>
                      <div className="text-sm text-gray-500">
                        #{(page - 1) * pageSize + index + 1} · ID: {process.id}
                      </div>
                    </div>
                    <div>
                      {actionLoadingId === process.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67]" />
                      ) : (
                        <div className="flex items-center gap-1">
                          {canAccess("processes.update") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEditProcess(process)}
                            >
                              <Pencil className="h-4 w-4 text-[#113F67]" />
                            </Button>
                          )}
                          {canAccess("processes.delete") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDeleteProcess(process.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">
                        {t.processes.table.type}
                      </span>
                      {process.type ? (
                        <Badge className={PROCESS_TYPE_BADGE_CLASS[process.type] ?? FALLBACK_PROCESS_BADGE_CLASS}>
                          {typeLabelBySlug[process.type] ?? process.type}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                    {process.description && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">
                          {t.processes.table.description}
                        </span>
                        <span className="text-gray-700 truncate ml-4">
                          {process.description}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">
                        {t.processes.table.date}
                      </span>
                      <span className="text-gray-700">
                        {moment(process.created_at).format(
                          "DD MMMM, YYYY hh:mm A"
                        )}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">
                    {t.processes.table.srNo}
                  </TableHead>
                  <TableHead>{t.processes.table.processName}</TableHead>
                  <TableHead>{t.processes.table.type}</TableHead>
                  <TableHead>{t.processes.table.description}</TableHead>
                  <TableHead>{t.processes.table.date}</TableHead>
                  <TableHead className="text-right">
                    {t.processes.table.actions}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {t.processes.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : processes.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-gray-500"
                    >
                      {t.processes.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  processes.map((process: ProcessesProps, index: number) => (
                    <TableRow className="align-top" key={process.id}>
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium text-gray-900">
                            {process.name || "-"}
                          </span>
                          <div className="text-sm text-gray-500">
                            ID: {process.id}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {process.type ? (
                          <Badge className={PROCESS_TYPE_BADGE_CLASS[process.type] ?? FALLBACK_PROCESS_BADGE_CLASS}>
                            {typeLabelBySlug[process.type] ?? process.type}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-600 max-w-xs truncate">
                        {process.description || "-"}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {moment(process.created_at).format(
                          "DD MMMM, YYYY hh:mm A"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {actionLoadingId === process.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67] ml-auto" />
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {canAccess("processes.update") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditProcess(process)}
                              >
                                <Pencil className="h-4 w-4 text-[#113F67]" />
                              </Button>
                            )}
                            {canAccess("processes.delete") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleDeleteProcess(process.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && processes.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalProcesses} {t.processes.pageOfLabel}
            </span>
            <span>{t.processes.pageSizeLabel}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${
                    page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""
                  }`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Process Form Modal */}
      <ProcessFormModal handleSaveProcess={handleSaveProcess} />
    </div>
  );
};

export default ProcessList;
